import { useEffect, useState } from 'react'
import { Bot, FileText, Plus, Settings2, Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { Input } from '@/components/shadcn/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { Textarea } from '@/components/shadcn/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/cn'
import { BUILTIN_COMMANDS, KIT_NAME, kitPath, template, type ClaudeKit, type KitItem, type KitKind } from './claudeKit'

const KINDS: { kind: KitKind; label: string; icon: typeof FileText; blurb: string }[] = [
  { kind: 'command', label: 'Commands', icon: FileText, blurb: 'Your own slash commands: /name runs the file as a prompt.' },
  { kind: 'skill', label: 'Skills', icon: Sparkles, blurb: 'Know-how Claude loads when a task matches its description; /name runs one directly.' },
  { kind: 'agent', label: 'Subagents', icon: Bot, blurb: 'Specialists Claude hands work to, each with its own instructions and tools.' },
]

type Draft = { kind: KitKind; name: string; text: string; mtime?: number; isNew: boolean }

/** The gear beside the message box: the project's commands, skills and subagents, to read and change. */
export function KitSettings({ kit }: { kit: ClaudeKit }) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<KitKind>('command')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const shown = kit.items.filter((i) => i.kind === kind)
  const meta = KINDS.find((k) => k.kind === kind)!

  // A file changed underneath (Claude, the terminal) while it was open and unedited: follow it.
  const live = draft && !draft.isNew ? kit.items.find((i) => i.path === kitPath(draft.kind, draft.name)) : undefined
  useEffect(() => { if (live && draft && live.mtime !== draft.mtime && draft.text === (kit.items.find((i) => i.path === live.path)?.text)) setDraft({ ...draft, mtime: live.mtime }) }, [live, draft, kit.items])

  const pick = (it: KitItem) => { setError(''); setDraft({ kind: it.kind, name: it.name, text: it.text, mtime: it.mtime, isNew: false }) }
  const create = () => { setError(''); setDraft({ kind, name: '', text: '', isNew: true }) }
  const save = async () => {
    if (!draft) return
    const name = draft.name.trim()
    if (!KIT_NAME.test(name)) return setError('Use lower-case letters, digits and dashes.')
    if (draft.isNew && kit.items.some((i) => i.kind === draft.kind && i.name === name)) return setError(`There is already one called ${name}.`)
    setBusy(true); setError('')
    try {
      const text = draft.text.trim() ? draft.text : template(draft.kind, name)
      await kit.save(draft.kind, name, text, draft.isNew ? undefined : draft.mtime)
      setDraft(null)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  const remove = async (it: KitItem) => {
    if (!confirm(`Delete ${it.path}? The deletion is in your next commit.`)) return
    setBusy(true)
    try { await kit.remove(it); if (draft?.name === it.name) setDraft(null) } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon-sm" className="size-7 rounded-full text-fg-subtle" aria-label="Commands, skills and subagents" onClick={() => setOpen(true)}><Settings2 /></Button>
        </TooltipTrigger>
        <TooltipContent>Commands, skills and subagents</TooltipContent>
      </Tooltip>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDraft(null) }}>
        <DialogContent className="flex h-[80vh] flex-col gap-3 bg-surface-3 p-4 sm:max-w-5xl shadow-lg">
          <DialogHeader className="gap-0.5">
            <DialogTitle className="text-base">Claude Code: commands, skills and subagents</DialogTitle>
            <DialogDescription className="text-xs">
              Files in this repo’s <code>.claude</code> folder, so every session and every clone gets them. A change here is in your next commit; other sessions see it once it is merged.
            </DialogDescription>
          </DialogHeader>
          <Tabs value={kind} onValueChange={(v) => { setKind(v as KitKind); setDraft(null) }}>
            <TabsList>
              {KINDS.map((k) => <TabsTrigger key={k.kind} value={k.kind} className="gap-1.5 text-xs"><k.icon className="size-3.5" />{k.label} <span className="text-muted-foreground">{kit.items.filter((i) => i.kind === k.kind).length}</span></TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="flex min-h-0 flex-1 gap-3">
            <div className="flex w-72 shrink-0 flex-col gap-2 overflow-y-auto">
              <p className="text-xs text-muted-foreground">{meta.blurb}</p>
              {kit.writable && <Button variant="outline" size="sm" className="justify-start gap-1.5 text-xs" onClick={create}><Plus className="size-3.5" />New {meta.label.toLowerCase().replace(/s$/, '')}</Button>}
              {shown.map((it) => (
                <button key={it.path} type="button" onClick={() => pick(it)}
                  className={cn('group rounded-lg border px-3 py-2 text-left text-xs transition-colors hover:border-ring/60', draft?.name === it.name && !draft.isNew && 'border-ring bg-accent/10')}>
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate font-mono">{kind === 'agent' ? it.name : `/${it.name}`}</span>
                    {kit.writable && <Trash2 className="size-3.5 opacity-0 transition-opacity group-hover:opacity-60 hover:!opacity-100" onClick={(e) => { e.stopPropagation(); void remove(it) }} aria-label={`Delete ${it.name}`} />}
                  </div>
                  <div className="mt-0.5 line-clamp-2 text-muted-foreground">{it.description || 'No description'}</div>
                </button>
              ))}
              {!shown.length && <p className="py-4 text-center text-xs text-fg-faint">None in this repo yet.</p>}
              {kind === 'command' && (
                <div className="mt-2 border-t pt-2">
                  <p className="mb-1 text-[11px] font-medium text-muted-foreground">Built into Claude Code</p>
                  {BUILTIN_COMMANDS.map((b) => <div key={b.name} className="truncate py-0.5 text-[11px] text-fg-faint"><span className="font-mono text-fg-subtle">/{b.name}</span> {b.description}</div>)}
                </div>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {draft ? <>
                <div className="flex items-center gap-2">
                  {draft.isNew
                    ? <Input autoFocus placeholder="name (lower-case, dashes)" value={draft.name} className="h-8 max-w-64 font-mono text-xs"
                        onChange={(e) => setDraft({ ...draft, name: e.target.value, text: draft.text || '' })} />
                    : <span className="font-mono text-xs">{kitPath(draft.kind, draft.name)}</span>}
                  {draft.isNew && draft.name && KIT_NAME.test(draft.name) && !draft.text &&
                    <Button variant="ghost" size="sm" className="text-xs" onClick={() => setDraft({ ...draft, text: template(draft.kind, draft.name) })}>Start from a template</Button>}
                  <span className="flex-1" />
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setDraft(null)}>Cancel</Button>
                  {kit.writable && <Button size="sm" className="text-xs" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>}
                </div>
                {error && <p className="text-xs text-err">{error}</p>}
                <Textarea value={draft.text} readOnly={!kit.writable} spellCheck={false}
                  placeholder={draft.isNew ? 'Leave empty to start from a template.' : ''}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  className="min-h-0 flex-1 resize-none font-mono text-xs leading-relaxed" />
              </> : <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed text-xs text-fg-faint">Pick one to read or change it{kit.writable ? ', or make a new one' : ''}.</div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
